import { useState, useEffect, useCallback } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Loader2, CalendarIcon, CheckCircle2, XCircle, Clock, Activity, Trash2, Ban } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useQualityAnalysisJob } from '@/hooks/useQualityAnalysisJobs';
import { useSalesUsers } from '@/hooks/useSalesUsers';
import { QualityScoreDetailDialog, TranscriptionDetailDialog } from '@/components/admin/QualityScoreDetailDialog';
import { toast } from 'sonner';

function DatePickerField({ label, value, onChange }: { label: string; value: Date | undefined; onChange: (d: Date | undefined) => void }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !value && "text-muted-foreground")}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? safeFormat(value, 'yyyy-MM-dd') : 'Selecionar'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={onChange} initialFocus className="p-3 pointer-events-auto" />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { icon: React.ReactNode; label: string; className: string }> = {
    pending: { icon: <Clock className="h-3 w-3" />, label: 'Pendente', className: 'bg-muted text-muted-foreground' },
    processing: { icon: <Activity className="h-3 w-3 animate-pulse" />, label: 'Processando', className: 'bg-primary/10 text-primary' },
    completed: { icon: <CheckCircle2 className="h-3 w-3" />, label: 'Concluído', className: 'bg-green-500/10 text-green-600' },
    failed: { icon: <XCircle className="h-3 w-3" />, label: 'Falhou', className: 'bg-destructive/10 text-destructive' },
    cancelled: { icon: <Ban className="h-3 w-3" />, label: 'Cancelado', className: 'bg-muted text-muted-foreground' },
  };
  const c = config[status] || config.pending;
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium", c.className)}>
      {c.icon} {c.label}
    </span>
  );
}

function JobProgressCard({ jobId, label, onComplete, onCancel }: { jobId: string | null; label: string; onComplete: () => void; onCancel: () => void }) {
  const { data: job } = useQualityAnalysisJob(jobId);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (job?.status === 'completed' || job?.status === 'failed' || job?.status === 'cancelled') {
      onComplete();
    }
  }, [job?.status, onComplete]);

  if (!jobId || !job) return null;

  const totalProcessed = (job.processed_items || 0) + (job.failed_items || 0) + ((job as any).skipped_items || 0);
  const progress = job.total_items ? Math.round((totalProcessed / job.total_items) * 100) : 0;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await supabase.from('quality_analysis_jobs').update({ status: 'cancelled' }).eq('id', jobId);
      onCancel();
      toast.success('Job cancelado');
    } catch {
      toast.error('Erro ao cancelar');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">{label}</CardTitle>
          <div className="flex items-center gap-2">
            <StatusBadge status={job.status} />
            {(job.status === 'processing' || job.status === 'pending') && (
              <Button variant="destructive" size="sm" onClick={handleCancel} disabled={cancelling}>
                {cancelling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                <span className="ml-1">Cancelar</span>
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Progress value={progress} className="h-2" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <span className="text-muted-foreground">Progresso:</span>{' '}
            <span className="font-medium">{totalProcessed}/{job.total_items || 0}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Processados:</span>{' '}
            <span className="font-medium text-green-600">{job.processed_items || 0}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Falhas:</span>{' '}
            <span className="font-medium text-destructive">{job.failed_items || 0}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Skipped:</span>{' '}
            <span className="font-medium text-muted-foreground">{(job as any).skipped_items || 0}</span>
          </div>
        </div>
        {(job as any).current_item_id && job.status === 'processing' && (
          <div className="text-xs text-muted-foreground">
            Item atual: <span className="font-mono">{(job as any).current_item_name || (job as any).current_item_id}</span>
          </div>
        )}
        {job.total_cost_usd != null && job.total_cost_usd > 0 && (
          <div className="text-xs text-muted-foreground">
            Custo acumulado: <span className="font-medium">${job.total_cost_usd.toFixed(4)}</span>
          </div>
        )}
        {job.error_message && (
          <div className="text-xs text-destructive bg-destructive/5 p-2 rounded">{job.error_message}</div>
        )}
      </CardContent>
    </Card>
  );
}

function ConsultantSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { salesUsers, isLoading } = useSalesUsers();
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-foreground">Consultor</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={isLoading ? 'Carregando...' : 'Todos os consultores'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos os consultores</SelectItem>
          {salesUsers.map(u => (
            <SelectItem key={u.id} value={u.id}>
              {u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || u.id.slice(0, 8)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function getScoreColor(score: number | null | undefined) {
  const s = score ?? 0;
  if (s >= 80) return { text: '#1B7A3D', bg: '#E8F5E9' };
  if (s >= 60) return { text: '#B8860B', bg: '#FFF8E1' };
  if (s >= 40) return { text: '#CC6600', bg: '#FFF3E0' };
  return { text: '#CC0000', bg: '#FFEBEE' };
}

export default function QualityAnalysisRunner() {
  // Shared
  const [selectedConsultant, setSelectedConsultant] = useState('all');

  // Transcribe state
  const [tCallId, setTCallId] = useState('');
  const [tDealId, setTDealId] = useState('');
  const [tStartDate, setTStartDate] = useState<Date>();
  const [tEndDate, setTEndDate] = useState<Date>();
  const [tMinDuration, setTMinDuration] = useState('30');
  const [tLimit, setTLimit] = useState('20');
  const [tLoading, setTLoading] = useState(false);
  const [tError, setTError] = useState('');
  const [activeTranscribeJobId, setActiveTranscribeJobId] = useState<string | null>(null);

  // Analyze state
  const [aDealId, setADealId] = useState('');
  const [aStartDate, setAStartDate] = useState<Date>();
  const [aEndDate, setAEndDate] = useState<Date>();
  const [aStageWon, setAStageWon] = useState(true);
  const [aStageLost, setAStageLost] = useState(true);
  const [aLimit, setALimit] = useState('50');
  const [aSkipAnalyzed, setASkipAnalyzed] = useState(true);
  const [aLoading, setALoading] = useState(false);
  const [aError, setAError] = useState('');
  const [activeAnalyzeJobId, setActiveAnalyzeJobId] = useState<string | null>(null);

  // Tab & Results state
  const [activeTab, setActiveTab] = useState('transcribe');
  const [qualityScores, setQualityScores] = useState<any[]>([]);
  const [transcriptions, setTranscriptions] = useState<any[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [selectedScore, setSelectedScore] = useState<any | null>(null);
  const [selectedTranscription, setSelectedTranscription] = useState<any | null>(null);

  // Results filters
  const [filterStageWon, setFilterStageWon] = useState(true);
  const [filterStageLost, setFilterStageLost] = useState(true);
  const [filterConsultant, setFilterConsultant] = useState('all');

  // Cleanup
  const [cleanupCount, setCleanupCount] = useState<number | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const fetchResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const [scoresRes, transRes] = await Promise.all([
        supabase.from('deal_quality_scores')
          .select('*, deal:deal_id(name, stage, sales_consultant, consultant:sales_consultant(display_name))')
          .order('analyzed_at', { ascending: false })
          .limit(100),
        supabase.from('call_transcriptions').select('*').order('created_at', { ascending: false }).limit(20),
      ]);
      if (scoresRes.data) setQualityScores(scoresRes.data);
      if (transRes.data) setTranscriptions(transRes.data);
    } catch (e) {
      console.error('Error fetching results:', e);
    } finally {
      setResultsLoading(false);
    }
  }, []);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  // Recover active jobs on mount
  useEffect(() => {
    const checkActiveJobs = async () => {
      const { data } = await supabase
        .from('quality_analysis_jobs')
        .select('id, job_type')
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false })
        .limit(2);

      if (data) {
        for (const job of data) {
          if (job.job_type === 'transcription') setActiveTranscribeJobId(job.id);
          if (job.job_type === 'analysis') setActiveAnalyzeJobId(job.id);
        }
      }
    };
    checkActiveJobs();
  }, []);

  const handleTranscribeCall = async () => {
    if (!tCallId.trim()) return;
    setTLoading(true); setTError('');
    try {
      const { data, error } = await supabase.functions.invoke('transcribe-calls', { body: { call_id: tCallId.trim() } });
      if (error) throw error;
      if (data?.job_id) setActiveTranscribeJobId(data.job_id);
    } catch (e: any) { setTError(e.message || 'Erro ao transcrever'); }
    finally { setTLoading(false); }
  };

  const handleTranscribeDeal = async () => {
    if (!tDealId.trim()) return;
    setTLoading(true); setTError('');
    try {
      const { data, error } = await supabase.functions.invoke('transcribe-calls', { body: { deal_id: tDealId.trim() } });
      if (error) throw error;
      if (data?.job_id) setActiveTranscribeJobId(data.job_id);
    } catch (e: any) { setTError(e.message || 'Erro ao transcrever'); }
    finally { setTLoading(false); }
  };

  const handleTranscribeBatch = async () => {
    setTLoading(true); setTError('');
    try {
      const body: any = { batch: { min_duration: Number(tMinDuration) || 30, limit: Number(tLimit) || 20 } };
      if (tStartDate) body.batch.start_date = safeFormat(tStartDate, 'yyyy-MM-dd');
      if (tEndDate) body.batch.end_date = safeFormat(tEndDate, 'yyyy-MM-dd');
      if (selectedConsultant && selectedConsultant !== 'all') body.batch.consultant_id = selectedConsultant;
      const { data, error } = await supabase.functions.invoke('transcribe-calls', { body });
      if (error) throw error;
      if (data?.job_id) setActiveTranscribeJobId(data.job_id);
    } catch (e: any) { setTError(e.message || 'Erro ao transcrever batch'); }
    finally { setTLoading(false); }
  };

  const handleAnalyzeDeal = async () => {
    if (!aDealId.trim()) return;
    setALoading(true); setAError('');
    try {
      const { data, error } = await supabase.functions.invoke('analyze-deal-quality', { body: { deal_id: aDealId.trim() } });
      if (error) throw error;
      if (data?.job_id) setActiveAnalyzeJobId(data.job_id);
    } catch (e: any) { setAError(e.message || 'Erro ao analisar'); }
    finally { setALoading(false); }
  };

  const handleAnalyzeBatch = async () => {
    setALoading(true); setAError('');
    try {
      const stages: string[] = [];
      if (aStageWon) stages.push('won');
      if (aStageLost) stages.push('lost');
      const body: any = { batch: { stages, limit: Number(aLimit) || 50, skip_analyzed: aSkipAnalyzed } };
      if (aStartDate) body.batch.start_date = safeFormat(aStartDate, 'yyyy-MM-dd');
      if (aEndDate) body.batch.end_date = safeFormat(aEndDate, 'yyyy-MM-dd');
      if (selectedConsultant && selectedConsultant !== 'all') body.batch.consultant_id = selectedConsultant;
      const { data, error } = await supabase.functions.invoke('analyze-deal-quality', { body });
      if (error) throw error;
      if (data?.job_id) setActiveAnalyzeJobId(data.job_id);
    } catch (e: any) { setAError(e.message || 'Erro ao analisar batch'); }
    finally { setALoading(false); }
  };

  const handleCheckCleanupCount = async () => {
    setCleanupLoading(true);
    try {
      const { data: deals } = await supabase.from('deals').select('id').is('sales_consultant', null);
      const dealIds = (deals || []).map(d => d.id);
      if (dealIds.length === 0) { setCleanupCount(0); setCleanupLoading(false); return; }
      const { count } = await supabase.from('deal_quality_scores').select('id', { count: 'exact', head: true }).in('deal_id', dealIds);
      setCleanupCount(count || 0);
    } catch { setCleanupCount(0); }
    finally { setCleanupLoading(false); }
  };

  const handleCleanup = async () => {
    setCleanupLoading(true);
    try {
      const { data: deals } = await supabase.from('deals').select('id').is('sales_consultant', null);
      const dealIds = (deals || []).map(d => d.id);
      if (dealIds.length > 0) {
        // Delete in chunks of 100
        for (let i = 0; i < dealIds.length; i += 100) {
          const chunk = dealIds.slice(i, i + 100);
          await supabase.from('deal_quality_scores').delete().in('deal_id', chunk);
        }
      }
      toast.success(`Scores limpos com sucesso`);
      setCleanupCount(null);
      fetchResults();
    } catch (e: any) { toast.error(e.message || 'Erro ao limpar'); }
    finally { setCleanupLoading(false); }
  };

  // Filter results client-side
  const filteredScores = qualityScores.filter(row => {
    const stage = (row.deal as any)?.stage;
    if (stage === 'won' && !filterStageWon) return false;
    if (stage === 'lost' && !filterStageLost) return false;
    if (filterConsultant && filterConsultant !== 'all') {
      const consultantId = (row.deal as any)?.sales_consultant;
      if (consultantId !== filterConsultant) return false;
    }
    return true;
  });

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-foreground">Quality Analysis Runner</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="transcribe">Transcrever Calls</TabsTrigger>
          <TabsTrigger value="analyze">Analisar Qualidade</TabsTrigger>
          <TabsTrigger value="results">Resultados</TabsTrigger>
        </TabsList>

        {/* === TRANSCRIBE === */}
        <TabsContent value="transcribe" className="space-y-4">
          <JobProgressCard
            jobId={activeTranscribeJobId}
            label="Transcrição em andamento"
            onComplete={() => { fetchResults(); setActiveTab('results'); }}
            onCancel={() => setActiveTranscribeJobId(null)}
          />

          <Card>
            <CardHeader><CardTitle>Transcrever por Call ID</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input placeholder="call_id" value={tCallId} onChange={e => setTCallId(e.target.value)} />
              <Button onClick={handleTranscribeCall} disabled={tLoading || !tCallId.trim() || !!activeTranscribeJobId}>
                {tLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Transcrever Call
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Transcrever por Deal</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input placeholder="deal_id" value={tDealId} onChange={e => setTDealId(e.target.value)} />
              <Button onClick={handleTranscribeDeal} disabled={tLoading || !tDealId.trim() || !!activeTranscribeJobId}>
                {tLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Transcrever Deal
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Transcrever Batch</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <DatePickerField label="Data Início" value={tStartDate} onChange={setTStartDate} />
                <DatePickerField label="Data Fim" value={tEndDate} onChange={setTEndDate} />
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Min Duração (s)</label>
                  <Input type="number" value={tMinDuration} onChange={e => setTMinDuration(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Limite</label>
                  <Input type="number" value={tLimit} onChange={e => setTLimit(e.target.value)} />
                </div>
              </div>
              <ConsultantSelect value={selectedConsultant} onChange={setSelectedConsultant} />
              <Button onClick={handleTranscribeBatch} disabled={tLoading || !!activeTranscribeJobId}>
                {tLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Transcrever Batch
              </Button>
            </CardContent>
          </Card>

          {tError && <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">{tError}</div>}
        </TabsContent>

        {/* === ANALYZE === */}
        <TabsContent value="analyze" className="space-y-4">
          <JobProgressCard
            jobId={activeAnalyzeJobId}
            label="Análise em andamento"
            onComplete={() => { fetchResults(); setActiveTab('results'); }}
            onCancel={() => setActiveAnalyzeJobId(null)}
          />

          <Card>
            <CardHeader><CardTitle>Analisar por Deal</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input placeholder="deal_id" value={aDealId} onChange={e => setADealId(e.target.value)} />
              <Button onClick={handleAnalyzeDeal} disabled={aLoading || !aDealId.trim() || !!activeAnalyzeJobId}>
                {aLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Analisar Deal
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Analisar Batch</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <DatePickerField label="Data Início" value={aStartDate} onChange={setAStartDate} />
                <DatePickerField label="Data Fim" value={aEndDate} onChange={setAEndDate} />
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Limite</label>
                  <Input type="number" value={aLimit} onChange={e => setALimit(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">Stages</label>
                  <div className="flex items-center gap-4 pt-2">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={aStageWon} onCheckedChange={(v) => setAStageWon(!!v)} /> Won
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={aStageLost} onCheckedChange={(v) => setAStageLost(!!v)} /> Lost
                    </label>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ConsultantSelect value={selectedConsultant} onChange={setSelectedConsultant} />
                <div className="space-y-1">
                  <label className="text-sm font-medium text-foreground">&nbsp;</label>
                  <label className="flex items-center gap-2 text-sm pt-2">
                    <Checkbox checked={aSkipAnalyzed} onCheckedChange={(v) => setASkipAnalyzed(!!v)} />
                    Excluir já analisados
                  </label>
                </div>
              </div>
              <Button onClick={handleAnalyzeBatch} disabled={aLoading || !!activeAnalyzeJobId}>
                {aLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Analisar Batch
              </Button>
            </CardContent>
          </Card>

          {aError && <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">{aError}</div>}
        </TabsContent>

        {/* === RESULTS === */}
        <TabsContent value="results" className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={filterStageWon} onCheckedChange={(v) => setFilterStageWon(!!v)} /> Won
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={filterStageLost} onCheckedChange={(v) => setFilterStageLost(!!v)} /> Lost
              </label>
            </div>
            <div className="w-64">
              <ConsultantSelect value={filterConsultant} onChange={setFilterConsultant} />
            </div>
            <div className="ml-auto flex gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" onClick={handleCheckCleanupCount} disabled={cleanupLoading}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Limpar Scores sem Consultor
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Limpar Scores sem Consultor</AlertDialogTitle>
                    <AlertDialogDescription>
                      {cleanupCount === null
                        ? 'Contando registros...'
                        : cleanupCount === 0
                          ? 'Nenhum score de deal sem consultor encontrado.'
                          : `Serão deletados ${cleanupCount} scores de deals sem consultor atribuído. Essa ação não pode ser desfeita.`}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    {cleanupCount != null && cleanupCount > 0 && (
                      <AlertDialogAction onClick={handleCleanup} disabled={cleanupLoading}>
                        {cleanupLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Confirmar Limpeza
                      </AlertDialogAction>
                    )}
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button variant="outline" onClick={fetchResults} disabled={resultsLoading}>
                {resultsLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Atualizar
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader><CardTitle>Deal Quality Scores ({filteredScores.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Deal</TableHead>
                      <TableHead>Consultor</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Overall</TableHead>
                      <TableHead>Consultant</TableHead>
                      <TableHead>SDR</TableHead>
                      <TableHead>Analyzed At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredScores.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Nenhum registro</TableCell></TableRow>
                    ) : filteredScores.map((row: any) => {
                      const deal = row.deal as any;
                      const scoreColor = getScoreColor(row.overall_score);
                      return (
                        <TableRow key={row.id || row.deal_id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedScore(row)}>
                          <TableCell className="text-xs font-medium max-w-[200px] truncate">
                            {deal?.name || row.deal_id?.slice(0, 8) + '...'}
                          </TableCell>
                          <TableCell className="text-xs">
                            {(deal?.consultant as any)?.display_name || '-'}
                          </TableCell>
                          <TableCell>
                            <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium",
                              deal?.stage === 'won' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
                            )}>{deal?.stage || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ color: scoreColor.text, backgroundColor: scoreColor.bg }}>
                              {row.overall_score}
                            </span>
                          </TableCell>
                          <TableCell>{row.consultant_overall_score}</TableCell>
                          <TableCell>{row.sdr_overall_score}</TableCell>
                          <TableCell className="text-xs">{row.analyzed_at ? safeFormat(new Date(row.analyzed_at), 'dd/MM/yyyy HH:mm') : '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Call Transcriptions (últimos 20)</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Call ID</TableHead>
                      <TableHead>Duração (s)</TableHead>
                      <TableHead>Confiança</TableHead>
                      <TableHead>Custo (USD)</TableHead>
                      <TableHead>Preview</TableHead>
                      <TableHead>Criado em</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transcriptions.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhum registro</TableCell></TableRow>
                    ) : transcriptions.map((row: any) => (
                      <TableRow key={row.id || row.call_id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedTranscription(row)}>
                        <TableCell className="font-mono text-xs">{row.call_id?.slice(0, 8)}...</TableCell>
                        <TableCell>{row.duration_seconds}</TableCell>
                        <TableCell>{row.confidence_score != null ? `${(row.confidence_score * 100).toFixed(1)}%` : '-'}</TableCell>
                        <TableCell>{row.cost_usd != null ? `$${row.cost_usd.toFixed(4)}` : '-'}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs">{row.transcription_text?.slice(0, 100) || '-'}</TableCell>
                        <TableCell className="text-xs">{row.created_at ? safeFormat(new Date(row.created_at), 'dd/MM/yyyy HH:mm') : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <QualityScoreDetailDialog
        score={selectedScore}
        open={!!selectedScore}
        onOpenChange={(open) => { if (!open) setSelectedScore(null); }}
      />
      <TranscriptionDetailDialog
        transcription={selectedTranscription}
        open={!!selectedTranscription}
        onOpenChange={(open) => { if (!open) setSelectedTranscription(null); }}
      />
    </div>
  );
}
